import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Container,
  Form,
  Header,
  Icon,
  Input,
  List,
  Message,
  Modal
} from 'semantic-ui-react';
import { useTheme } from './ThemeContext';
import { useToast } from './utils/Toast';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import { isMockAuthEnabled, clearMockSession, getMockSites, getMockUsers } from './utils/mockAuth';
import { handleSessionExpired } from './utils/sessionHandler';
import {
  fetchAccessibleSites,
  shareSiteWithUser,
  unshareSiteWithUser,
  SiteRecord,
  AuthTokenExpiredError,
  AccessDeniedError
} from './api/sites';
import {
  ClientUserSummary,
  fetchManagedClients,
  inviteClientAccount,
  deleteClientAccount
} from './api/users';
import './SiteDashboard.css';

interface ViewState {
  loading: boolean;
  error: string | null;
}

interface InviteFormState {
  email: string;
  fullName: string;
}

const ManagerDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, role, refreshSession, loading: authLoading } = useAuth();

  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [viewState, setViewState] = useState<ViewState>({ loading: true, error: null });
  const [searchQuery, setSearchQuery] = useState('');

  const [clients, setClients] = useState<ClientUserSummary[]>([]);
  const [clientsLoading, setClientsLoading] = useState<boolean>(true);

  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const [inviteForm, setInviteForm] = useState<InviteFormState>({ email: '', fullName: '' });
  const [inviteSubmitting, setInviteSubmitting] = useState<boolean>(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSearchQuery, setInviteSearchQuery] = useState<string>('');
  const [deletingClientId, setDeletingClientId] = useState<string | null>(null);

  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [shareTargetSite, setShareTargetSite] = useState<SiteRecord | null>(null);
  const [shareSearchQuery, setShareSearchQuery] = useState<string>('');
  const [shareSelectionIds, setShareSelectionIds] = useState<string[]>([]);
  const [shareAssignedIds, setShareAssignedIds] = useState<string[]>([]);
  const [shareSubmitting, setShareSubmitting] = useState<boolean>(false);
  const roleRedirectRef = useRef(false);

  const logoSrc = theme === 'dark'
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  const loadSites = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (role !== 'admin_client') {
      setViewState({ loading: false, error: null });
      setSites([]);
      return;
    }

    setViewState({ loading: true, error: null });

    try {
      // Return mock data if mock auth is enabled
      if (isMockAuthEnabled()) {
        const mockSites = getMockSites() as any;
        setSites(mockSites);
        setViewState({ loading: false, error: null });
        return;
      }
      
      const response = await fetchAccessibleSites();
      setSites(response);
      setViewState({ loading: false, error: null });
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        handleSessionExpired(showToast);
        return;
      }

      if (error instanceof AccessDeniedError) {
        setViewState({ loading: false, error: null });
        showToast('You do not have permission to view these sites.', 'error');
        navigate('/no-access', { replace: true, state: { from: '/manager-dashboard' } });
        return;
      }

      const message = error instanceof Error ? error.message : 'Unable to load sites at the moment.';
      setViewState({ loading: false, error: message });
      showToast(message, 'error');
    }
  }, [authLoading, navigate, role, showToast]);

  const loadClients = useCallback(async () => {
    if (authLoading) {
      return;
    }

    setClientsLoading(true);

    try {
      if (role !== 'admin_client') {
        setClients([]);
        return;
      }

      // Return mock data if mock auth is enabled
      if (isMockAuthEnabled()) {
        const mockUsers = getMockUsers().filter(u => u.role === 'client') as any;
        setClients(mockUsers);
        setClientsLoading(false);
        return;
      }

      const response = await fetchManagedClients();
      setClients(response);
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        handleSessionExpired(showToast);
        return;
      }

      if (error instanceof AccessDeniedError) {
        showToast('You do not have permission to manage client accounts.', 'error');
        navigate('/no-access', { replace: true, state: { from: '/manager-dashboard' } });
        return;
      }

      const message = error instanceof Error ? error.message : 'Unable to load clients right now.';
      showToast(message, 'error');
    } finally {
      setClientsLoading(false);
    }
  }, [authLoading, navigate, role, showToast]);

  useEffect(() => {
    if (!authLoading) {
      void loadSites();
      void loadClients();
    }
  }, [authLoading, loadSites, loadClients]);

  useEffect(() => {
    if (authLoading || roleRedirectRef.current) {
      return;
    }

    if (!role) {
      return;
    }

    if (role === 'admin_client') {
      return;
    }

    roleRedirectRef.current = true;

    if (role === 'admin') {
      navigate('/dashboard', { replace: true, state: { from: '/manager-dashboard' } });
      return;
    }

    showToast('You do not have permission to view this dashboard.', 'error');
    navigate('/no-access', { replace: true, state: { from: '/manager-dashboard' } });
  }, [authLoading, navigate, role, showToast]);

  const handleLogout = async () => {
    try {
      if (isMockAuthEnabled()) {
        clearMockSession();
        await refreshSession();
        showToast('Logged out successfully', 'success');
        navigate('/');
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      await refreshSession();
      showToast('Logged out successfully', 'success');
      navigate('/');
    } catch (err) {
      console.error('[ManagerDashboard] Failed to sign out', err);
      showToast('Unable to log out right now. Please try again.', 'error');
    }
  };

  const handleSiteClick = (siteId: string) => {
    navigate(`/sites/${siteId}`);
  };

  const filteredSites = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return sites;
    }

    return sites.filter((site) => {
      const nameMatch = site.siteName?.toLowerCase().includes(query);
      const locationMatch = site.location?.toLowerCase().includes(query);
      return Boolean(nameMatch || locationMatch);
    });
  }, [sites, searchQuery]);

  const shareAssignedSet = useMemo(() => new Set(shareAssignedIds), [shareAssignedIds]);
  const shareSelectionSet = useMemo(() => new Set(shareSelectionIds), [shareSelectionIds]);

  const shareHasChanges = useMemo(() => {
    if (shareAssignedIds.length !== shareSelectionIds.length) {
      return true;
    }

    const selectionSet = new Set(shareSelectionIds);
    return shareAssignedIds.some((id) => !selectionSet.has(id));
  }, [shareAssignedIds, shareSelectionIds]);

  const filteredShareClients = useMemo(() => {
    const query = shareSearchQuery.trim().toLowerCase();

    if (!query) {
      return clients;
    }

    return clients.filter((client) => {
      const name = client.fullName?.toLowerCase() ?? '';
      const email = client.email.toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [clients, shareSearchQuery]);

  const filteredInviteClients = useMemo(() => {
    const query = inviteSearchQuery.trim().toLowerCase();
    if (!query) {
      return clients;
    }



    return clients.filter((client) => {
      const name = client.fullName?.toLowerCase() ?? '';
      const email = client.email.toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [clients, inviteSearchQuery]);

  const handleShareSelectAll = () => {
    const nextIds = filteredShareClients.length > 0 ? filteredShareClients.map((client) => client.id) : [];
    if (nextIds.length === 0) {
      setShareSelectionIds([]);
      return;
    }

    setShareSelectionIds((prev) => {
      const merged = new Set(prev);
      nextIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  };

  const handleShareReset = () => {
    setShareSelectionIds([...shareAssignedIds]);
  };

  const shareResetDisabled = useMemo(() => {
    if (shareAssignedIds.length !== shareSelectionIds.length) {
      return false;
    }

    const assignedSet = new Set(shareAssignedIds);
    return shareSelectionIds.every((id) => assignedSet.has(id));
  }, [shareAssignedIds, shareSelectionIds]);

  const openShareModal = (site: SiteRecord) => {
  const assigned = (site.selectedUsers ?? []).map((value) => String(value));
  setShareTargetSite(site);
  setShareAssignedIds(assigned);
  setShareSelectionIds(assigned);
    setShareSearchQuery('');
    setShareModalOpen(true);
  };

  const resetShareModalState = () => {
    setShareModalOpen(false);
    setShareTargetSite(null);
    setShareAssignedIds([]);
    setShareSelectionIds([]);
    setShareSearchQuery('');
  };

  const closeShareModal = (force = false) => {
    if (shareSubmitting && !force) {
      return;
    }

    resetShareModalState();
  };

  const toggleShareSelection = (clientId: string, nextValue?: boolean) => {
    setShareSelectionIds((prev) => {
      const selection = new Set(prev);
      const shouldSelect = nextValue ?? !selection.has(clientId);

      if (shouldSelect) {
        selection.add(clientId);
      } else {
        selection.delete(clientId);
      }

      return Array.from(selection);
    });
  };

  const handleShareSubmit = async () => {
    if (!shareTargetSite) {
      return;
    }

    const assignedSet = new Set(shareAssignedIds);
    const desiredSet = new Set(shareSelectionIds);

    const toAdd = Array.from(desiredSet).filter((id) => !assignedSet.has(id));
    const toRemove = Array.from(assignedSet).filter((id) => !desiredSet.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      showToast('No access changes to apply.', 'info');
      return;
    }

    setShareSubmitting(true);

    try {
      let latestSite: SiteRecord | null = null;

      for (const clientId of toAdd) {
        latestSite = await shareSiteWithUser(shareTargetSite.id, clientId);
      }

      for (const clientId of toRemove) {
        latestSite = await unshareSiteWithUser(shareTargetSite.id, clientId);
      }

      if (latestSite) {
        setSites((prev) => prev.map((existing) => (existing.id === latestSite?.id ? latestSite : existing)));
      }

      let successMessage = 'Client access updated.';
      if (toAdd.length > 0 && toRemove.length > 0) {
        successMessage = 'Client access updated.';
      } else if (toAdd.length > 0) {
        successMessage = toAdd.length === 1 ? 'Client access granted.' : 'Access granted for selected clients.';
      } else if (toRemove.length > 0) {
        successMessage = toRemove.length === 1 ? 'Client access revoked.' : 'Access revoked for selected clients.';
      }

  showToast(successMessage, 'success');
  closeShareModal(true);
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        showToast(error.message, 'warning');
        void refreshSession();
        navigate('/');
        return;
      }

      if (error instanceof AccessDeniedError) {
        showToast('You do not have permission to update sharing for this site.', 'error');
        return;
      }

      const message = error instanceof Error ? error.message : 'Failed to update site access.';
      showToast(message, 'error');
    } finally {
      setShareSubmitting(false);
    }
  };

  const assignedClientsForSite = (site: SiteRecord) => {
    const assignedIds: string[] = (site.selectedUsers ?? []).map((value) => String(value));
    return assignedIds
      .map((userId) => clients.find((client) => client.id === userId) ?? null)
      .filter((client): client is ClientUserSummary => Boolean(client));
  };

  const unresolvedAssignmentsCount = (site: SiteRecord) => {
    const assignedIds = new Set<string>((site.selectedUsers ?? []).map((value) => String(value)));
    const resolvedIds = new Set<string>(clients.map((client) => client.id));
    let unresolved = 0;
    assignedIds.forEach((id) => {
      if (!resolvedIds.has(id)) {
        unresolved += 1;
      }
    });
    return unresolved;
  };

  const handleInviteSubmit = async () => {
    const trimmedEmail = inviteForm.email.trim();

    if (!trimmedEmail) {
      setInviteError('Email is required.');
      return;
    }

    const normalizedEmail = trimmedEmail.toLowerCase();
    const trimmedFullName = inviteForm.fullName.trim();
    const payloadFullName = trimmedFullName.length > 0 ? trimmedFullName : undefined;

    setInviteSubmitting(true);
    setInviteError(null);

    try {
      const userSummary = await inviteClientAccount({
        email: normalizedEmail,
        fullName: payloadFullName
      });

      setClients((prev) => {
        const existingIndex = prev.findIndex((client) => client.id === userSummary.id);
        if (existingIndex >= 0) {
          const next = [...prev];
          next[existingIndex] = userSummary;
          return next;
        }
        return [userSummary, ...prev];
      });

      const normalizedSummaryEmail = userSummary.email?.trim().toLowerCase() ?? null;
      const invitationLabel = payloadFullName
        ?? userSummary.fullName?.trim()
        ?? (normalizedSummaryEmail === normalizedEmail && userSummary.email ? userSummary.email : trimmedEmail);

      showToast(`Invitation sent to ${invitationLabel}`, 'success');
      setInviteForm({ email: '', fullName: '' });
      setInviteModalOpen(false);
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        showToast(error.message, 'warning');
        void refreshSession();
        navigate('/');
        return;
      }

      if (error instanceof AccessDeniedError) {
        showToast('You do not have permission to invite client accounts.', 'error');
        navigate('/no-access', { replace: true, state: { from: '/manager-dashboard' } });
        return;
      }

      const message = error instanceof Error ? error.message : 'Unable to send invitation.';
      setInviteError(message);
      showToast(message, 'error');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleDeleteClient = async (clientId: string, clientEmail: string) => {
    if (deletingClientId) return; // Prevent multiple simultaneous deletions

    setDeletingClientId(clientId);
    
    try {
      await deleteClientAccount(clientId);
      
      // Remove client from the list
      setClients((prev) => prev.filter((client) => client.id !== clientId));
      
      showToast(`Client ${clientEmail} deleted successfully`, 'success');
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        showToast(error.message, 'warning');
        void refreshSession();
        navigate('/');
        return;
      }
      
      if (error instanceof AccessDeniedError) {
        showToast('You do not have permission to delete this client', 'error');
        return;
      }
      
      const message = error instanceof Error ? error.message : 'Unable to delete client.';
      showToast(message, 'error');
    } finally {
      setDeletingClientId(null);
    }
  };

  const inviteModal = (
    
    <Modal
      className="invite-client-modal compact-modal"
      open={inviteModalOpen}
      onClose={() => {
        if (!inviteSubmitting) {
          setInviteModalOpen(false);
          setInviteError(null);
          setInviteSearchQuery('');
        }
      }}
      size="small"
      
    >
      <Modal.Header style={{ display: 'flex', alignItems: 'left' }}>
        <Icon name="user plus" style={{ marginRight: '0.75rem' }} />
        <span>Invite a user</span>
        <div style={{ position: 'absolute', right: '1rem', top: '1rem', cursor: "pointer" }} className="close icon">
          <Icon name="close" onClick={() => { if (!inviteSubmitting) { setInviteModalOpen(false); setInviteError(null); } }} />
        </div>
      </Modal.Header>
      <Modal.Content style={{ padding: "0" }}>
        <div style={{ padding: "1.5rem"  }}> {/* fake modal.content */}
          <div className="share-modal-invite-section">
            <Form>
              <Form.Input
                label="Client email"
                placeholder="client@example.com"
                type="email"
                required
                value={inviteForm.email}
                onChange={(_event, data) => setInviteForm((prev) => ({ ...prev, email: data.value ?? '' }))}
              />
              <Form.Input
                label="Client name (optional)"
                placeholder="Full name"
                value={inviteForm.fullName}
                onChange={(_event, data) => setInviteForm((prev) => ({ ...prev, fullName: data.value ?? '' }))}
              />
            </Form>
            <div className="share-modal-invite-button-section">
              <Button
                primary
                loading={inviteSubmitting}
                onClick={() => { void handleInviteSubmit(); }}
                disabled={inviteSubmitting || !inviteForm.email.trim()}
              >
                <Icon name="paper plane" /> Send invite
              </Button>
            </div>
          </div>
        </div>
        
        <div className='fake-modal-header'>
          <Icon name="users" style={{ marginRight: '0.75rem' }} />
          <span>Manage Clients</span>
        </div>

        <div style={{ padding: "1.5rem" }}>
          <div className="share-modal-search-section">
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Search invited clients..."
              value={inviteSearchQuery}
              onChange={(_event, data) => setInviteSearchQuery(typeof data.value === 'string' ? data.value : '')}
              fluid
              className="share-modal-search-input"
            />
          </div>
          <div className="share-user-list-container">
            {clients.length === 0 ? (
              <div className="share-no-results">
                <Icon name="user outline" size="large" />
                <p>No clients invited yet.</p>
              </div>
            ) : filteredInviteClients.length === 0 ? (
              <div className="share-no-results">
                <Icon name="search" size="large" />
                <p>No invited clients match your search.</p>
              </div>
            ) : (
              <List divided selection className="share-user-selection-list">
                {filteredInviteClients.map((client) => (
                  <List.Item key={client.id} className="share-user-item">
                    <Icon name="user circle" size="large" className="share-user-avatar" />
                    <List.Content>
                      <List.Header className="share-user-name">{client.fullName ?? client.email}</List.Header>
                      <List.Description className="share-user-email">{client.email}</List.Description>
                    </List.Content>
                    <List.Content floated="right">
                      <Icon
                        name="close"
                        size="large"
                        className={`delete-client-button ${deletingClientId === client.id ? 'deleting' : ''}`}
                        style={{
                          cursor: deletingClientId ? 'not-allowed' : 'pointer',
                          color: deletingClientId === client.id ? '#ccc' : '#666',
                          transition: 'color 0.2s ease'
                        }}
                        onClick={() => {
                          if (!deletingClientId) {
                            void handleDeleteClient(client.id, client.email);
                          }
                        }}
                        onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                          if (!deletingClientId) {
                            (e.target as HTMLElement).style.color = '#e74c3c';
                          }
                        }}
                        onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                          if (!deletingClientId) {
                            (e.target as HTMLElement).style.color = '#666';
                          }
                        }}
                      />
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
          </div>
        </div>
        {inviteError && (
          <Message negative className="share-error-message">
            <Message.Header>Unable to send invitation</Message.Header>
            <p>{inviteError}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => { if (!inviteSubmitting) { setInviteModalOpen(false); setInviteError(null); } }} disabled={inviteSubmitting}>
          Cancel
        </Button>
      </Modal.Actions>
    </Modal>
  );

  const shareModal = (
    <Modal
      className="share-access-modal"
      open={shareModalOpen}
      onClose={() => closeShareModal()}
      size="small"
    >
      <Modal.Header style={{ display: 'flex', alignItems: 'left' }}>
        <Icon name="users" style={{ marginRight: '0.75rem' }} />
        <span>{shareTargetSite?.siteName ? `Manage access for ${shareTargetSite.siteName}` : 'Manage site access'}</span>
        <div style={{ position: 'absolute', right: '1rem', top: '1rem', cursor: "pointer" }} className="close icon">
          <Icon name="close" onClick={closeShareModal} />
        </div>
      </Modal.Header>
      <Modal.Content>
        {clients.length === 0 ? (
          <div className="share-empty-state">
            <Icon name="user plus" size="huge" />
            <p>Invite a user before sharing access.</p>
          </div>
        ) : (
          <>
            <div className="share-modal-search-section">
              <Input
                icon="search"
                iconPosition="left"
                placeholder="Search invited clients..."
                value={shareSearchQuery}
                onChange={(_event, data) => setShareSearchQuery(typeof data.value === 'string' ? data.value : '')}
                fluid
                className="share-modal-search-input"
              />
              <div className="share-modal-bulk-actions">
                <Button
                  type="button"
                  size="small"
                  className="bulk-action-btn"
                  onClick={handleShareSelectAll}
                  disabled={filteredShareClients.length === 0}
                >
                  Select All ({filteredShareClients.length})
                </Button>
                <Button
                  type="button"
                  size="small"
                  className="bulk-action-btn"
                  onClick={handleShareReset}
                  disabled={shareResetDisabled}
                >
                  Reset
                </Button>
              </div>
            </div>
            <div className="share-user-list-container">
              {filteredShareClients.length === 0 ? (
                <div className="share-no-results">
                  <Icon name="search" size="large" />
                  <p>No invited clients match your search.</p>
                </div>
              ) : (
                <List divided selection className="share-user-selection-list">
                  {filteredShareClients.map((client) => {
                    const hasAccess = shareSelectionSet.has(client.id);
                    const wasAssigned = shareAssignedSet.has(client.id);
                    const willRemove = wasAssigned && !hasAccess;

                    return (
                      <List.Item
                        key={client.id}
                        className={`share-user-item${hasAccess ? ' selected' : ''}${willRemove ? ' pending-removal' : ''}`}
                        onClick={() => {
                          toggleShareSelection(client.id);
                        }}
                      >
                        <List.Content floated="right">
                          <Checkbox
                            toggle
                            checked={hasAccess}
                            onChange={(event, data) => {
                              event.stopPropagation();
                              toggleShareSelection(client.id, Boolean(data.checked));
                            }}
                            aria-label={hasAccess
                              ? `Disable access for ${client.fullName ?? client.email}`
                              : `Enable access for ${client.fullName ?? client.email}`}
                          />
                        </List.Content>
                        <Icon name="user circle" size="large" className="share-user-avatar" />
                        <List.Content>
                          <List.Header className="share-user-name">
                            {client.fullName ? client.fullName : client.email}
                          </List.Header>
                          <List.Description className="share-user-email">
                            {client.email}
                          </List.Description>
                          <div className={`share-user-status${willRemove ? ' removing' : hasAccess ? ' active' : ' inactive'}`}>
                            {willRemove ? 'Will remove access' : hasAccess ? 'Has access' : 'No access'}
                          </div>
                        </List.Content>
                      </List.Item>
                    );
                  })}
                </List>
              )}
            </div>
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => closeShareModal()} disabled={shareSubmitting}>
          Cancel
        </Button>
        <Button
          primary
          onClick={() => { void handleShareSubmit(); }}
          loading={shareSubmitting}
          disabled={shareSubmitting || !shareHasChanges}
        >
          <Icon name="save" /> Apply changes
        </Button>
      </Modal.Actions>
    </Modal>
  );

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Container>
          <div className="header-content">
            <div className="header-logo">
              <img src={logoSrc} alt="Pilbara Risk Solutions" className="dashboard-logo" />
            </div>
            <div className="header-actions">
              <div className="header-user" title={user?.email ?? undefined}>
                <Icon name="user circle" />
                <span className="user-name">{user?.user_metadata?.full_name?.trim() || user?.email || 'Member'}</span>
              </div>
              <Button
                className="logout-button"
                onClick={handleLogout}
                icon
                labelPosition="right"
              >
                <Icon name="sign out" />
                Logout
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container className="dashboard-main">
        <div className="dashboard-title-section">
          <Header as="h1" className="dashboard-title">
            <Icon name="briefcase" className="dashboard-title-icon" />
            Manager Dashboard
          </Header>
          <p className="dashboard-subtitle">
            Manage your client invites and share dashboards with a single click.
          </p>
        </div>

        <div className="dashboard-controls">
          <div className="search-container">
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Search your sites..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="search-input"
              size="large"
            />
          </div>
          <Button
            primary
            size="large"
            icon
            labelPosition="left"
            className="create-button"
            onClick={() => { setInviteModalOpen(true); setInviteError(null); }}
          >
            <Icon name="user plus" />
            Invite user
          </Button>
        </div>

        <div className="sites-grid">
          {viewState.loading ? (
            <div className="loading-message">Loading your sites...</div>
          ) : viewState.error ? (
            <Message negative>
              <Message.Header>Unable to load sites</Message.Header>
              <p>{viewState.error}</p>
              <Button basic color="red" onClick={() => { void loadSites(); }} className="retry-button">
                <Icon name="refresh" /> Try again
              </Button>
            </Message>
          ) : filteredSites.length === 0 ? (
            <div className="empty-message">
              <Icon name="archive" size="huge" className="empty-icon" />
              <p>No sites available yet.</p>
            </div>
          ) : (
            filteredSites.map((site) => {
              const hazardCount = site.hazardEntries?.length ?? 0;
              const assignedClients = assignedClientsForSite(site);
              const unresolvedCount = unresolvedAssignmentsCount(site);
              const shareButtonDisabled = clients.length === 0 || clientsLoading;
              const shareButtonLoading = shareSubmitting && shareTargetSite?.id === site.id;

              return (
                <Card
                  key={site.id}
                  className="site-card"
                  onClick={() => handleSiteClick(site.id)}
                >
                  <Card.Content>
                    <Card.Header className="site-card-header">
                      {site.siteName || 'Untitled site'}
                    </Card.Header>
                    <Card.Meta className="site-card-meta">
                      <Icon name="map marker alternate" aria-hidden="true" />
                      <span>{site.location || 'Location not specified'}</span>
                    </Card.Meta>
                    <Card.Description className="site-card-description">
                      <div className="site-info-row">
                        <span className="info-label">Created</span>
                        <span className="info-value">
                          {site.createdAt ? new Date(site.createdAt).toLocaleDateString() : 'Unknown'}
                        </span>
                      </div>
                      <div className="site-info-row">
                        <span className="info-label">Hazards</span>
                        <span className="info-value">{hazardCount}
                        </span>
                      </div>
                      <div className="site-info-row">
                        <span className="info-label">Shared clients</span>
                        <span className="info-value">{assignedClients.length + unresolvedCount}</span>
                      </div>
                    </Card.Description>
                  </Card.Content>
                  <Card.Content extra onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}>
                    <div className="site-card-actions">
                      <Button
                        icon
                        labelPosition="left"
                        className="manage-access-button create-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!shareButtonDisabled) { 
                            openShareModal(site);
                          } else if (clients.length === 0 && !clientsLoading) { 
                            showToast('Invite a user before sharing access.', 'info');
                          }
                        }}
                        disabled={shareButtonDisabled || shareButtonLoading}
                        loading={shareButtonLoading}
                      >
                        <Icon name="users" color='grey'/>
                        Manage access
                      </Button>
                    </div>
                  </Card.Content>
                </Card>
              );
            })
          )}
        </div>

        {filteredSites.length > 0 && !viewState.loading && !viewState.error && (
          <div className="dashboard-footer">
            <p>Showing {filteredSites.length} of {sites.length} sites</p>
          </div>
        )}

      </Container>

      {inviteModal}
      {shareModal}
    </div>
  );
};

export default ManagerDashboard;
